import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type StagedActionSummary } from '../api.ts';

/**
 * Writes that have not happened.
 *
 * Every row here is a call a step wanted to make and reef refused to make on
 * its own: the model was handed a ticket, the server was never contacted, and
 * the effect exists only as the sentence in the middle column.
 *
 * The screen is deliberately one sentence per row. A reviewer deciding from a
 * JSON blob is a reviewer approving whatever it says, so the arguments are
 * behind a disclosure and the sentence is what is on the page.
 */
const STATES = ['staged', 'applied', 'rejected', 'failed', 'reverted'] as const;

export function ActionsView() {
  const [state, setState] = useState<(typeof STATES)[number]>('staged');
  const [actions, setActions] = useState<StagedActionSummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  /*
   * Which request the screen is currently showing.
   *
   * Switching tabs starts a second list while the first is still in flight, and
   * the two do not have to land in order — so the queue would fill with the
   * previous tab's rows, or, far more often, be emptied by them. "Applied" then
   * reads "Nothing applied yet" over a row that is sitting right there in the
   * response, until somebody presses Refresh.
   */
  const latest = useRef(0);

  const refresh = useCallback(() => {
    const request = ++latest.current;
    api
      .listActions({ state })
      .then((result) => {
        if (request === latest.current) setActions(result.actions);
      })
      .catch((error) => {
        if (request === latest.current) setNote((error as Error).message);
      });
  }, [state]);

  useEffect(refresh, [refresh]);

  const act = async (id: string, label: string, work: () => Promise<string>) => {
    setBusy(`${id}:${label}`);
    setNote(null);
    try {
      setNote(await work());
      refresh();
    } catch (error) {
      setNote((error as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Approvals</p>
        {STATES.map((entry) => (
          <button
            key={entry}
            className="list-item"
            aria-current={entry === state}
            onClick={() => setState(entry)}
          >
            {LABELS[entry]}
          </button>
        ))}
      </aside>

      <main className="main">
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{LABELS[state]}</h1>
        <p className="hint" style={{ marginTop: 0 }}>
          {DESCRIPTIONS[state]}
        </p>

        {note && (
          <div className="banner">
            <p style={{ margin: 0 }}>{note}</p>
          </div>
        )}

        {actions.length === 0 ? (
          <div className="empty">Nothing {state === 'staged' ? 'waiting' : `${state} yet`}.</div>
        ) : (
          <div className="stack">
            {actions.map((action) => (
              <div className="card" key={action.id}>
                <p style={{ margin: '0 0 0.4rem' }}>{action.description}</p>

                <p className="hint" style={{ margin: '0 0 0.6rem' }}>
                  <code>{action.connectionId}</code>
                  {action.resources.length > 0 && ` · ${action.resources.join(', ')}`}
                  {action.runId && ` · run ${action.runId}`}
                  {action.requestedBy && ` · asked for by ${action.requestedBy}`} ·{' '}
                  {new Date(action.createdAt).toLocaleString()}
                  {action.decidedBy && ` · decided by ${action.decidedBy}`}
                </p>

                {action.error && (
                  <p className="hint" style={{ margin: '0 0 0.6rem' }}>
                    {action.error}
                  </p>
                )}

                <details
                  open={expanded === action.id}
                  onToggle={(event) =>
                    setExpanded((event.currentTarget as HTMLDetailsElement).open ? action.id : null)
                  }
                >
                  <summary className="hint">Exact call</summary>
                  <pre style={{ overflowX: 'auto' }}>
                    {action.mount}.{action.method}({JSON.stringify(action.args, null, 2)})
                  </pre>
                </details>

                {action.state === 'staged' && (
                  <div className="row wrap">
                    <button
                      className="primary"
                      disabled={busy !== null}
                      onClick={() =>
                        act(action.id, 'apply', async () => {
                          const result = await api.applyAction(action.id);
                          return result.applied
                            ? 'Applied. The call ran under your own access.'
                            : `Not applied: ${result.reason}`;
                        })
                      }
                    >
                      Apply
                    </button>
                    <button
                      disabled={busy !== null}
                      onClick={() =>
                        act(action.id, 'reject', async () => {
                          await api.rejectAction(action.id);
                          return 'Rejected. The step was told nothing happened.';
                        })
                      }
                    >
                      Reject
                    </button>
                  </div>
                )}

                {action.state === 'applied' && (
                  <div className="row">
                    <button
                      disabled={busy !== null}
                      onClick={() =>
                        act(action.id, 'revert', async () => {
                          await api.revertAction(action.id);
                          return 'Marked as reverted. Reef did not undo anything — it records that you did.';
                        })
                      }
                    >
                      Mark as reverted
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="row">
          <button onClick={refresh}>Refresh</button>
        </div>
      </main>
    </div>
  );
}

const LABELS: Record<(typeof STATES)[number], string> = {
  staged: 'Waiting for you',
  applied: 'Applied',
  rejected: 'Rejected',
  failed: 'Failed',
  reverted: 'Reverted',
};

const DESCRIPTIONS: Record<(typeof STATES)[number], string> = {
  staged:
    'These have not happened. Applying one runs the call under your own access, so you can only approve what you could have done yourself.',
  applied: 'These ran. The audit trail records who asked and who approved.',
  rejected: 'These were refused. The step was told the write did not happen.',
  failed: 'Approved, then the call itself failed. Nothing was retried automatically.',
  reverted:
    'Applied and then undone by a person. Reef never synthesizes an inverse call — this records what someone did by hand.',
};
